import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { plainToClass } from 'class-transformer'

import { Button, Checkbox, Grid, TextField, Typography } from '@material-ui/core'
import TreeItem from '@material-ui/lab/TreeItem'
import TreeView from '@material-ui/lab/TreeView'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import ArrowRightIcon from '@material-ui/icons/ArrowRight'
import ArrowRightAlt from '@material-ui/icons/ArrowRightAlt'
import { makeStyles } from '@material-ui/core/styles'
import { Theme } from '@material-ui/core/styles/createMuiTheme'

import { urlFor } from '../../routes'
import { useForm } from '../../forms'
import { arrayRemove } from '../../utils'
import { guidLookup, Hierarchy } from '../../api/models'

const Label = styled.div`
  display: flex;
  align-items: center;
  padding: ${(props) => props.theme.spacing(0.5)}px;
`

const IconContainer = styled.div`
  margin-right: ${(props) => props.theme.spacing(1)}px;
`

const useStyles = makeStyles((theme: Theme) => ({
  addText: {
    color: 'grey',
    fontWeight: 'inherit',
  },
  labelText: {
    fontWeight: 'inherit',
  },
  navLink: {
    fontWeight: 'inherit',
    marginLeft: theme.spacing(2),
  },
}))

interface SelectedObject {
  tier: number
  item: Hierarchy
}

export class Selected {
  selected: { [key: string]: boolean }
  subs: { [key: string]: () => void }
  parentSubs: { [key: string]: () => void }
  // For the actual hierarchy elements that will need to be re-rendered
  nodeSubs: { [key: string]: () => void }
  selectedObj: { [key: string]: SelectedObject }
  tiers: Hierarchy[][]
  tierCb: (() => void)[][]

  constructor() {
    this.selected = {}
    this.subs = {}
    this.parentSubs = {}
    this.nodeSubs = {}
    this.selectedObj = {}
    this.tiers = []
    this.tierCb = []
  }

  sub = (key: string, cb: () => void) => {
    this.subs[key] = cb
  }

  unsub = (key: string) => {
    delete this.subs[key]
  }

  subParent = (key: string, cb: () => void) => {
    this.parentSubs[key] = cb
  }

  unsubParent = (key: string) => {
    delete this.parentSubs[key]
  }

  subNode = (key: string, cb: () => void) => {
    this.nodeSubs[key] = cb
  }

  unsubNode = (key: string) => {
    delete this.nodeSubs[key]
  }

  notifyNode = (key: string) => {
    const cb = this.nodeSubs[key]
    if (cb) {
      cb()
    }
  }

  registerHierarchyTiers = (roots: Hierarchy[], tier: number = 0) => {
    for (const item of roots) {
      this.addToTier(item, tier)
      if (item.children?.length) {
        this.registerHierarchyTiers(item.children, tier + 1)
      }
    }
  }

  addToTier = (item: Hierarchy, tier: number) => {
    if (!this.tiers[tier]) {
      this.tiers[tier] = []
    }
    this.tiers[tier].push(item)
  }

  subTier = (tier: number, cb: () => void) => {
    if (!this.tierCb[tier]) {
      this.tierCb[tier] = []
    }
    this.tierCb[tier].push(cb)
  }

  unsubTier = (tier: number, cb: () => void) => {
    arrayRemove(this.tiers[tier] || [], cb)
  }

  alertTier = (tier: number) => {
    if (this.tierCb[tier]) {
      for (const cb of this.tierCb[tier]) {
        cb()
      }
    }
  }

  setTier = (tier: number, value: boolean) => {
    const parentCbs: (() => void)[] = []
    for (const item of this.tiers[tier]) {
      const key = item.guid as string
      this.selected[key] = value
      if (value) {
        this.selectedObj[key] = { tier, item }
      } else {
        delete this.selectedObj[key]
      }
      const cb = this.subs[key]
      if (cb) {
        cb()
      }
      const parentCb = this.parentSubs[key]
      if (parentCb && parentCbs.indexOf(parentCb) < 0) {
        parentCbs.push(parentCb)
      }
    }
    for (const cb of parentCbs) {
      cb()
    }
    this.alertTier(tier)
    // special global subscription
    const globalCb = this.subs.global
    if (globalCb) {
      globalCb()
    }
  }

  isTierSet = (tier: number) => {
    return (this.tiers[tier] || []).reduce((value, item: Hierarchy) => {
      return value && this.isSet(item.guid as string)
    }, true)
  }

  set = (item: Hierarchy, tier: number, value: boolean) => {
    const key = item.guid as string
    this.selected[key] = value
    if (value) {
      this.selectedObj[key] = { tier, item }
    } else {
      delete this.selectedObj[key]
    }
    const cb = this.subs[key]
    if (cb) {
      cb()
    }
    const parentCb = this.parentSubs[key]
    if (parentCb) {
      parentCb()
    }
    // special global subscription
    const globalCb = this.subs.global
    if (globalCb) {
      globalCb()
    }

    this.alertTier(tier)
  }

  isSet = (key: string) => {
    return !!this.selected[key]
  }

  deselectAll = () => {
    for (const { tier, item } of Object.values(this.selectedObj)) {
      this.set(item, tier, false)
    }
  }

  anySelected = () => {
    return Object.values(this.selected).reduce((value: boolean, currentValue) => {
      return value || currentValue
    }, false)
  }
}

interface TierCheckboxProps {
  tier: number
  selected: Selected
}

const TierCheckbox = (props: TierCheckboxProps) => {
  const { tier, selected } = props
  const [, refresh] = useState({})

  const select = (event: React.ChangeEvent<HTMLInputElement>) => {
    selected.setTier(tier, event.target.checked)
  }

  useEffect(() => {
    const cb = () => refresh({})
    selected.subTier(tier, cb)
    return () => {
      selected.unsubTier(tier, cb)
    }
  }, [selected, tier])

  return <Checkbox checked={selected.isTierSet(tier)} onChange={select} />
}

interface AllCheckboxProps {
  tier: number
  item: Hierarchy
  selected: Selected
}

const AllCheckbox = (props: AllCheckboxProps) => {
  const { item, selected, tier } = props
  const [, refresh] = useState({})

  const select = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked
    for (const child of item.children || []) {
      selected.set(child, tier, checked)
    }
    // trigger an update but don't include in true values
    refresh({})
  }

  useEffect(() => {
    const cb = () => refresh({})
    for (const child of item.children || []) {
      selected.subParent(child.guid as string, cb)
    }
    return () => {
      for (const child of item.children || []) {
        selected.subParent(child.guid as string, cb)
      }
    }
  }, [item.children, selected])

  return (
    <Checkbox
      checked={item.children?.reduce((val: boolean, currentVal) => {
        return val && selected.isSet(currentVal.guid as string)
      }, true)}
      onChange={select}
    />
  )
}

interface ConnectedCheckboxProps {
  tier: number
  item: Hierarchy
  selected: Selected
}

const ConnectedCheckbox = (props: ConnectedCheckboxProps) => {
  const { item, selected, tier } = props
  const [, refresh] = useState({})

  const select = (event: React.ChangeEvent<HTMLInputElement>) => {
    selected.set(item, tier, event.target.checked)
  }

  useEffect(() => {
    selected.sub(item.guid as string, () => refresh({}))
    return () => {
      selected.unsub(item.guid as string)
    }
  }, [item.guid, selected])

  return <Checkbox checked={selected.isSet(item.guid as string)} onChange={select} />
}

interface HierarchyItemProps {
  tier: number
  item: Hierarchy
  selected: Selected
  edit?: boolean
}

const HierarchyItem = (props: HierarchyItemProps) => {
  const { item, edit = false, selected, tier } = props
  const Class = guidLookup(item.guid as string)
  const Icon = Class ? Class.Icon : undefined
  const url = Class ? urlFor(plainToClass(Class, { xid: item.guid })) : null
  const [, refresh] = useState({})

  const classes = useStyles()

  const [show, setShow] = useState(false)

  useEffect(() => {
    selected.subNode(item.guid as string, () => refresh({}))
    return () => {
      selected.unsubNode(item.guid as string)
    }
  }, [item.guid, selected])

  // Hackery to get the TreeItem to render without the expand icon when there
  // are no children
  const children = []
  if (edit && !!item.children?.length && item.children.length > 1) {
    children.push(
      <TreeItem
        key={`${item.guid}.select-all-tier`}
        nodeId={`${item.guid}.select-all-tier`}
        label={
          <Label>
            <div onClick={(event: React.SyntheticEvent) => event.stopPropagation()}>
              <TierCheckbox tier={tier + 1} selected={selected} />
            </div>
            <Typography className={classes.labelText}>Select All At This Level</Typography>
          </Label>
        }
      />
    )
    children.push(
      <TreeItem
        key={`${item.guid}.select-all`}
        nodeId={`${item.guid}.select-all`}
        label={
          <Label>
            <div onClick={(event: React.SyntheticEvent) => event.stopPropagation()}>
              <AllCheckbox item={item} tier={tier + 1} selected={selected} />
            </div>
            <Typography className={classes.labelText}>Select All Children</Typography>
          </Label>
        }
      />
    )
  }
  if (item.children?.length) {
    children.push(
      ...item.children.map((child) => (
        <HierarchyItem
          key={child.guid}
          item={child}
          edit={edit}
          selected={selected}
          tier={tier + 1}
        />
      ))
    )
  }

  return (
    <TreeItem
      nodeId={item.guid as string}
      label={
        <Label onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
          {edit && (
            <div onClick={(event: React.SyntheticEvent) => event.stopPropagation()}>
              <ConnectedCheckbox item={item} selected={selected} tier={tier} />
            </div>
          )}
          {Icon && (
            <IconContainer>
              <Icon />
            </IconContainer>
          )}
          <Typography className={classes.labelText}>{item.label}</Typography>
          {item.toCreate && (
            <Typography className={classes.addText}>&nbsp;(to be added)</Typography>
          )}
          {!edit && show && url && (
            <Button
              className={classes.navLink}
              color="primary"
              size="small"
              component={Link}
              to={url}
              endIcon={<ArrowRightAlt />}
            >
              Go
            </Button>
          )}
        </Label>
      }
    >
      {children}
    </TreeItem>
  )
}

interface AddFormData {
  name: string
  start: number
  end: number
}

interface ActionsProps {
  selected: Selected
  edit: boolean
}

const Actions = (props: ActionsProps) => {
  const { edit, selected } = props
  const [, refresh] = useState({})

  useEffect(() => {
    selected.sub('global', () => refresh({}))
    return () => {
      selected.unsub('global')
    }
  }, [selected])

  const { Form, Field, data } = useForm()

  const onSubmit = (data: AddFormData) => {
    const allSelected = [...Object.values(selected.selectedObj)]
    for (const { tier, item } of allSelected) {
      let index = +(data.start || 1)
      const end = +(data.end || 1)
      if (!item.children) {
        item.children = []
      }
      let guidIndex = item.children.length
      while (index <= end) {
        const label = data.name.replace('{index}', index.toString())
        const guid = `${item.guid}.${guidIndex}`
        const newObj = plainToClass(Hierarchy, { guid, label, toCreate: true })
        item.children.push(newObj)
        selected.addToTier(newObj, tier + 1)
        guidIndex++
        index++
      }
      selected.set(item, tier, false)
      selected.notifyNode(item.guid as string)
    }
  }

  const hasIndex = (data.getValue('name') || '').includes('{index}')

  if (!edit || !selected.anySelected()) {
    return null
  }
  return (
    <>
      <Typography>Actions</Typography>
      <Form onSubmit={onSubmit}>
        <Grid container spacing={6}>
          <Grid item>
            <Field
              name="name"
              defaultValue=""
              renderField={({ name, value, setValue }) => (
                <TextField
                  id={name}
                  name={name}
                  label="Name"
                  value={value}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setValue(name, e.target.value)
                    refresh({})
                  }}
                />
              )}
            />
          </Grid>
          {hasIndex && (
            <>
              <Grid item>
                <Field
                  name="start"
                  defaultValue={1}
                  renderField={({ name, value, setValue }) => (
                    <TextField
                      id={name}
                      name={name}
                      label="Starting Index"
                      value={value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setValue(name, e.target.value)
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <Field
                  name="end"
                  defaultValue={1}
                  renderField={({ name, value, setValue }) => (
                    <TextField
                      id={name}
                      name={name}
                      label="Ending Index"
                      value={value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setValue(name, e.target.value)
                      }
                    />
                  )}
                />
              </Grid>
            </>
          )}
          <Button type="submit">Add</Button>
        </Grid>
      </Form>
    </>
  )
}

interface HierarchyTreeProps {
  roots: Hierarchy[]
  edit?: boolean
}

const HierarchyTree = (props: HierarchyTreeProps) => {
  const { roots, edit = false } = props
  const [selected] = useState(new Selected())

  useEffect(() => {
    if (roots?.length) {
      selected.registerHierarchyTiers(roots)
    }
  }, [roots, selected])
  return (
    <>
      <TreeView defaultCollapseIcon={<ArrowDropDownIcon />} defaultExpandIcon={<ArrowRightIcon />}>
        {roots &&
          roots.map((root) => (
            <HierarchyItem key={root.guid} item={root} edit={edit} selected={selected} tier={0} />
          ))}
      </TreeView>
      <Actions selected={selected} edit={edit} />
    </>
  )
}

export default HierarchyTree
